import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Search, 
  Filter, 
  Plus, 
  Edit2, 
  Copy,
  CheckSquare,
  Square,
  Percent,
  Trash2,
  AlertTriangle,
  Calendar,
  Download
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import ImageUpload from '../../components/ImageUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Switch
} from '../../components/ui/switch';
import { adminService } from '../../services/adminService';
import { ExportDialog } from '../../components/exports/ExportDialog';
import { toast } from 'sonner';

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' }
];

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Batch selection state
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [batchEditOpen, setBatchEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await adminService.getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (product) => {
    try {
      const result = await adminService.toggleProductStatus(product.id);
      setProducts(prev => 
        prev.map(p => p.id === product.id ? { ...p, status: result.new_status } : p)
      );
      toast.success(result.message);
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update product status');
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (selectedProduct) {
        await adminService.updateProduct(selectedProduct.id, productData);
        toast.success('Product updated successfully');
      } else {
        await adminService.createProduct(productData);
        toast.success('Product created successfully');
      }
      loadProducts();
      setEditDialogOpen(false);
      setCreateDialogOpen(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleDuplicateProduct = async (product) => {
    try {
      const result = await adminService.duplicateProduct(product.id);
      toast.success(result.message);
      loadProducts();
    } catch (error) {
      console.error('Error duplicating product:', error);
      toast.error('Failed to duplicate product');
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    try {
      await adminService.deleteProduct(productToDelete.id);
      toast.success('Product deleted successfully');
      setProducts(prev => prev.filter(p => p.id !== productToDelete.id));
      setDeleteConfirmOpen(false);
      setProductToDelete(null);
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleBatchUpdate = async (updates) => {
    try {
      const result = await adminService.batchUpdateProducts(
        Array.from(selectedProductIds),
        updates
      );
      toast.success(result.message);
      setSelectedProductIds(new Set());
      setBatchEditOpen(false);
      loadProducts();
    } catch (error) {
      console.error('Error batch updating:', error);
      toast.error('Failed to update products');
    }
  };

  const handleBatchDelete = async () => {
    try {
      const result = await adminService.batchDeleteProducts(
        Array.from(selectedProductIds)
      );
      toast.success(result.message);
      setSelectedProductIds(new Set());
      setBatchDeleteOpen(false);
      loadProducts();
    } catch (error) {
      console.error('Error batch deleting:', error);
      toast.error('Failed to delete products');
    }
  };

  // Selection handlers
  const toggleSelectProduct = (productId) => {
    setSelectedProductIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProductIds.size === filteredProducts.length) {
      setSelectedProductIds(new Set());
    } else {
      setSelectedProductIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const categories = [...new Set(products.map(p => p.category))];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const activeCount = products.filter(p => p.status === 'available').length;
  const inactiveCount = products.filter(p => p.status === 'inactive').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-products">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-600 mt-1">
            {activeCount} active, {inactiveCount} inactive
          </p>
        </div>
        <div className="flex gap-2">
          {selectedProductIds.size > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={() => setBatchEditOpen(true)}
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit ({selectedProductIds.size})
              </Button>
              <Button 
                variant="outline"
                onClick={() => setBatchDeleteOpen(true)}
                className="border-red-500 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedProductIds.size})
              </Button>
            </>
          )}
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Product</DialogTitle>
                <DialogDescription className="sr-only">Add New Product dialog. Use the form below to continue.</DialogDescription>
              </DialogHeader>
              <ProductForm 
                onSave={handleSaveProduct} 
                onCancel={() => setCreateDialogOpen(false)} 
              />
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            onClick={() => setExportOpen(true)}
            data-testid="export-products-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="available">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-4 w-10">
                    <button onClick={toggleSelectAll} className="flex items-center justify-center">
                      {selectedProductIds.size === filteredProducts.length && filteredProducts.length > 0 ? (
                        <CheckSquare className="h-5 w-5 text-amber-500" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Wholesale</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Retail</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Tax</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Pack</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Delivery Days</th>
                  <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase">Active</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredProducts.map((product) => {
                  const isSelected = selectedProductIds.has(product.id);
                  return (
                    <tr 
                      key={product.id} 
                      className={`hover:bg-gray-50 ${product.status === 'inactive' ? 'opacity-60' : ''} ${isSelected ? 'bg-amber-50' : ''}`}
                    >
                      <td className="p-4">
                        <button onClick={() => toggleSelectProduct(product.id)} className="flex items-center justify-center">
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-amber-500" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                              <Package className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{product.name}</p>
                            {product.is_seasonal && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Seasonal</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-gray-600 capitalize">{product.category}</td>
                      <td className="p-4">
                        <p className="font-medium text-gray-900">${product.wholesale_price?.toFixed(2) || '0.00'}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-gray-600">
                          {product.retail_price ? `$${product.retail_price.toFixed(2)}` : '-'}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-gray-600">
                          {product.sales_tax ? `${product.sales_tax}%` : '-'}
                        </p>
                      </td>
                      <td className="p-4 text-gray-600">{product.pack_size}</td>
                      <td className="p-4">
                        <AvailableDaysBadge days={product.available_days} />
                      </td>
                      <td className="p-4">
                        <div className="flex justify-center">
                          <Switch
                            checked={product.status === 'available'}
                            onCheckedChange={() => handleToggleStatus(product)}
                            data-testid={`toggle-${product.id}`}
                          />
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDuplicateProduct(product)}
                            title="Duplicate Product"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Dialog open={editDialogOpen && selectedProduct?.id === product.id} onOpenChange={(open) => {
                            setEditDialogOpen(open);
                            if (!open) setSelectedProduct(null);
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setSelectedProduct(product)}
                                title="Edit Product"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Edit Product</DialogTitle>
                                <DialogDescription className="sr-only">Edit Product dialog. Use the form below to continue.</DialogDescription>
                              </DialogHeader>
                              <ProductForm 
                                product={selectedProduct}
                                onSave={handleSaveProduct} 
                                onCancel={() => {
                                  setEditDialogOpen(false);
                                  setSelectedProduct(null);
                                }} 
                              />
                            </DialogContent>
                          </Dialog>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setProductToDelete(product);
                              setDeleteConfirmOpen(true);
                            }}
                            title="Delete Product"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No products found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch Edit Dialog */}
      <Dialog open={batchEditOpen} onOpenChange={setBatchEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Batch Edit {selectedProductIds.size} Products</DialogTitle>
            <DialogDescription className="sr-only">Batch Edit {selectedProductIds.size} Products dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          <BatchEditForm 
            onSave={handleBatchUpdate}
            onCancel={() => setBatchEditOpen(false)}
            categories={categories}
          />
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Product
            </DialogTitle>
            <DialogDescription className="sr-only">Delete Product dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>{productToDelete?.name}</strong>? 
              This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={handleDeleteProduct}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Batch Delete Confirmation Dialog */}
      <Dialog open={batchDeleteOpen} onOpenChange={setBatchDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {selectedProductIds.size} Products
            </DialogTitle>
            <DialogDescription className="sr-only">Delete {selectedProductIds.size} Products dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>{selectedProductIds.size} products</strong>? 
              This action cannot be undone.
            </p>
            <div className="mt-3 p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-700">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Warning: All selected products will be permanently removed.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBatchDeleteOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={handleBatchDelete}
            >
              Delete All Selected
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export CSV Dialog */}
      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entity="products"
        title="Export Products"
        description="Choose columns to include. Active page filters are honored."
        supportsDateRange={false}
        extraFilters={{
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(categoryFilter !== 'all' ? { category: categoryFilter } : {}),
          ...(searchQuery ? { search: searchQuery } : {}),
        }}
        extraFilterSummary={[
          ...(statusFilter !== 'all' ? [{ label: 'Status', value: statusFilter }] : []),
          ...(categoryFilter !== 'all' ? [{ label: 'Category', value: categoryFilter }] : []),
          ...(searchQuery ? [{ label: 'Search', value: searchQuery }] : []),
        ]}
      />
    </div>
  );
};

// Product Form Component
const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || '',
    wholesale_price: product?.wholesale_price || '',
    retail_price: product?.retail_price || '',
    unit_cost: product?.unit_cost ?? '',
    sales_tax: product?.sales_tax || '',
    pack_size: product?.pack_size || 'Box of 6',
    pack_quantity: product?.pack_quantity || 6,
    allergens: product?.allergens?.join(', ') || '',
    is_seasonal: product?.is_seasonal || false,
    image_url: product?.image_url || '',
    available_days: product?.available_days || []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      wholesale_price: parseFloat(formData.wholesale_price) || 0,
      retail_price: formData.retail_price ? parseFloat(formData.retail_price) : null,
      unit_cost: formData.unit_cost === '' ? null : parseFloat(formData.unit_cost),
      sales_tax: formData.sales_tax ? parseFloat(formData.sales_tax) : 0,
      pack_quantity: parseInt(formData.pack_quantity),
      allergens: formData.allergens ? formData.allergens.split(',').map(a => a.trim()) : [],
      available_days: formData.available_days
    });
  };

  const toggleDay = (day) => {
    setFormData(prev => {
      const currentDays = prev.available_days || [];
      if (currentDays.includes(day)) {
        return { ...prev, available_days: currentDays.filter(d => d !== day) };
      } else {
        return { ...prev, available_days: [...currentDays, day] };
      }
    });
  };

  const selectAllDays = () => {
    setFormData(prev => ({ ...prev, available_days: [] })); // Empty = all days
  };

  const clearAllDays = () => {
    setFormData(prev => ({ ...prev, available_days: DAYS_OF_WEEK.map(d => d.value) }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="name">Product Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
          />
        </div>
        <div>
          <Label htmlFor="category">Category</Label>
          <Input
            id="category"
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            placeholder="e.g., glazed, filled"
            required
          />
        </div>
        <div>
          <Label htmlFor="wholesale_price">Wholesale Price ($)</Label>
          <Input
            id="wholesale_price"
            type="number"
            step="0.01"
            value={formData.wholesale_price}
            onChange={(e) => setFormData(prev => ({ ...prev, wholesale_price: e.target.value }))}
            required
          />
        </div>
        <div>
          <Label htmlFor="retail_price">Retail Price ($)</Label>
          <Input
            id="retail_price"
            type="number"
            step="0.01"
            value={formData.retail_price}
            onChange={(e) => setFormData(prev => ({ ...prev, retail_price: e.target.value }))}
            placeholder="Optional"
          />
        </div>
        <div>
          <Label htmlFor="unit_cost">Unit Cost ($)</Label>
          <Input
            id="unit_cost"
            type="number"
            step="0.01"
            min="0"
            value={formData.unit_cost}
            onChange={(e) => setFormData(prev => ({ ...prev, unit_cost: e.target.value }))}
            placeholder="What it costs you to make one"
            data-testid="product-unit-cost"
          />
          <p className="text-xs text-gray-500 mt-1">Drives margin and waste reporting</p>
        </div>
        <div>
          <Label htmlFor="sales_tax">Sales Tax (%)</Label>
          <Input
            id="sales_tax"
            type="number"
            step="0.01"
            value={formData.sales_tax}
            onChange={(e) => setFormData(prev => ({ ...prev, sales_tax: e.target.value }))}
            placeholder="e.g., 8.5"
          />
        </div>
        <div>
          <Label htmlFor="pack_size">Pack Size</Label>
          <Input
            id="pack_size"
            value={formData.pack_size}
            onChange={(e) => setFormData(prev => ({ ...prev, pack_size: e.target.value }))}
            placeholder="e.g., Box of 6"
          />
        </div>
        <div>
          <Label htmlFor="pack_quantity">Quantity per Pack</Label>
          <Input
            id="pack_quantity"
            type="number"
            value={formData.pack_quantity}
            onChange={(e) => setFormData(prev => ({ ...prev, pack_quantity: e.target.value }))}
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="allergens">Allergens (comma-separated)</Label>
          <Input
            id="allergens"
            value={formData.allergens}
            onChange={(e) => setFormData(prev => ({ ...prev, allergens: e.target.value }))}
            placeholder="e.g., wheat, dairy, eggs"
          />
        </div>
        
        {/* Available Delivery Days */}
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-600" />
              Available Delivery Days
            </Label>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={selectAllDays}
                className="text-xs h-7"
              >
                All Days
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            {formData.available_days.length === 0 
              ? "Available for delivery on all days of the week"
              : "Product can only be ordered for delivery on selected days"
            }
          </p>
          <div className="flex flex-wrap gap-2">
            {DAYS_OF_WEEK.map(day => {
              const isSelected = formData.available_days.length === 0 || formData.available_days.includes(day.value);
              const isRestricted = formData.available_days.length > 0;
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    isSelected && isRestricted
                      ? 'bg-amber-500 border-amber-500 text-white'
                      : isSelected && !isRestricted
                      ? 'bg-green-100 border-green-300 text-green-700'
                      : 'bg-gray-100 border-gray-200 text-gray-400'
                  }`}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
          {formData.available_days.length > 0 && (
            <p className="text-xs text-amber-600 mt-2">
              Limited to: {formData.available_days.map(d => 
                DAYS_OF_WEEK.find(day => day.value === d)?.label
              ).join(', ')}
            </p>
          )}
        </div>
        
        <div className="col-span-2">
          <ImageUpload
            currentImage={formData.image_url}
            onImageUploaded={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
            folder="products"
          />
        </div>
        <div className="col-span-2 flex items-center gap-2">
          <Switch
            id="is_seasonal"
            checked={formData.is_seasonal}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_seasonal: checked }))}
          />
          <Label htmlFor="is_seasonal">Seasonal Product</Label>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-amber-500 hover:bg-amber-600">
          {product ? 'Update Product' : 'Create Product'}
        </Button>
      </div>
    </form>
  );
};

// Available Days Badge Component
const AvailableDaysBadge = ({ days }) => {
  if (!days || days.length === 0) {
    return (
      <Badge className="bg-green-100 text-green-700 text-xs">
        All Days
      </Badge>
    );
  }
  
  const shortDays = days.map(d => {
    const day = DAYS_OF_WEEK.find(day => day.value === d);
    return day?.label || d.substring(0, 3);
  });
  
  if (days.length <= 3) {
    return (
      <Badge className="bg-amber-100 text-amber-700 text-xs">
        {shortDays.join(', ')}
      </Badge>
    );
  }
  
  return (
    <Badge className="bg-amber-100 text-amber-700 text-xs" title={shortDays.join(', ')}>
      {days.length} days
    </Badge>
  );
};

// Batch Edit Form Component
const BatchEditForm = ({ onSave, onCancel, categories }) => {
  const [formData, setFormData] = useState({
    category: '',
    wholesale_price: '',
    retail_price: '',
    sales_tax: '',
    pack_size: '',
    is_seasonal: null,
    status: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Only include fields that have values
    const updates = {};
    if (formData.category) updates.category = formData.category;
    if (formData.wholesale_price) updates.wholesale_price = parseFloat(formData.wholesale_price);
    if (formData.retail_price) updates.retail_price = parseFloat(formData.retail_price);
    if (formData.sales_tax) updates.sales_tax = parseFloat(formData.sales_tax);
    if (formData.pack_size) updates.pack_size = formData.pack_size;
    if (formData.is_seasonal !== null) updates.is_seasonal = formData.is_seasonal;
    if (formData.status) updates.status = formData.status;
    
    if (Object.keys(updates).length === 0) {
      toast.error('Please fill in at least one field to update');
      return;
    }
    
    onSave(updates);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-gray-600 bg-amber-50 p-3 rounded-lg">
        <Percent className="h-4 w-4 inline mr-2 text-amber-600" />
        Only filled fields will be updated. Leave fields empty to keep existing values.
      </p>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="batch_category">Category</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
            <SelectTrigger id="batch_category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="batch_status">Status</Label>
          <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
            <SelectTrigger id="batch_status">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="batch_wholesale">Wholesale Price ($)</Label>
          <Input
            id="batch_wholesale"
            type="number"
            step="0.01"
            value={formData.wholesale_price}
            onChange={(e) => setFormData(prev => ({ ...prev, wholesale_price: e.target.value }))}
            placeholder="Leave empty to keep"
          />
        </div>
        <div>
          <Label htmlFor="batch_retail">Retail Price ($)</Label>
          <Input
            id="batch_retail"
            type="number"
            step="0.01"
            value={formData.retail_price}
            onChange={(e) => setFormData(prev => ({ ...prev, retail_price: e.target.value }))}
            placeholder="Leave empty to keep"
          />
        </div>
        <div>
          <Label htmlFor="batch_tax">Sales Tax (%)</Label>
          <Input
            id="batch_tax"
            type="number"
            step="0.01"
            value={formData.sales_tax}
            onChange={(e) => setFormData(prev => ({ ...prev, sales_tax: e.target.value }))}
            placeholder="e.g., 8.5"
          />
        </div>
        <div>
          <Label htmlFor="batch_pack">Pack Size</Label>
          <Input
            id="batch_pack"
            value={formData.pack_size}
            onChange={(e) => setFormData(prev => ({ ...prev, pack_size: e.target.value }))}
            placeholder="e.g., Box of 6"
          />
        </div>
        <div className="col-span-2">
          <Label>Seasonal Status</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="is_seasonal"
                checked={formData.is_seasonal === null}
                onChange={() => setFormData(prev => ({ ...prev, is_seasonal: null }))}
                className="text-amber-500"
              />
              <span className="text-sm">No change</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="is_seasonal"
                checked={formData.is_seasonal === true}
                onChange={() => setFormData(prev => ({ ...prev, is_seasonal: true }))}
                className="text-amber-500"
              />
              <span className="text-sm">Seasonal</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="is_seasonal"
                checked={formData.is_seasonal === false}
                onChange={() => setFormData(prev => ({ ...prev, is_seasonal: false }))}
                className="text-amber-500"
              />
              <span className="text-sm">Not Seasonal</span>
            </label>
          </div>
        </div>
      </div>
      
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-amber-500 hover:bg-amber-600">
          Apply to Selected
        </Button>
      </div>
    </form>
  );
};

export default AdminProducts;
